/* eslint-disable import/prefer-default-export */
import { ref, computed, watch, effectScope } from 'vue';

// Composable computes macro-derived fields from sub tasks and keeps the macro element updated via watchers
export function useMacroElement(planning) {
  const getSubTasksRecursive = (el, macroSubEls) => {
    if (macroSubEls.has(el.id)) return null; // browse node once
    macroSubEls.add(el.id);

    const subEls = [];
    const subTasksId = new Set(el.getSubTasks());
    const subTasks = planning.elements.filter(item => subTasksId.has(parseInt(item.id, 10)));

    subTasks.forEach((subTask) => {
      if (macroSubEls.has(subTask.id)) return; // browse node once

      if (! subTask.getSubTasks()) {
        macroSubEls.add(subTask.id);
        subEls.push(subTask);
      } else {
        subEls.push(...getSubTasksRecursive(subTask, macroSubEls));
      }
    });
    return subEls;
  };

  const createMacroComputed = (elRef) => {
    const macroElements = computed(() => getSubTasksRecursive(elRef?.value, new Set()) || []); // get all descendants tasks

    const macroStartTime = computed(() => {
      const el = elRef?.value;
      if (! el.isType('macro')) return null;
      if (! macroElements.value.length) return el.getStartTime(); // macro has no subtasks
      const subTasksStartTime = macroElements.value.map(subTask => subTask.getStartTime()).filter(item => item);
      return moment.min(subTasksStartTime);
    });

    const macroEndTime = computed(() => {
      const el = elRef?.value;
      if (! el.isType('macro')) return null;
      if (! macroElements.value.length) return el.getEndTime();
      const subTasksEndTime = macroElements.value.map(subTask => subTask.getEndTime()).filter(item => item);
      return moment.max(subTasksEndTime);
    });

    const macroProgress = computed(() => {
      const el = elRef?.value;
      if (! el.isType('macro')) return null;
      let sum = 0;
      let count = 0;
      macroElements.value.forEach((subTask) => {
        if (! subTask.isType('macro', 'task')) return;
        sum += subTask.getProgress();
        count++;
      });
      return count ? Math.round(sum / count) : 0; // 0 is default value
    });

    const macroUsers = computed(() => {
      const el = elRef?.value;
      if (! el.isType('macro')) return null;
      if (! macroElements.value.length) return null;
      const subTasksUsers = macroElements.value.map(subTask => subTask.getUsers());
      const subTasksAllUsers = [].concat(...subTasksUsers.filter(users => users?.length));

      // Users
      const users = subTasksAllUsers.filter(user => user.id);
      const uniqueUsers = {};
      const usersOccurence = {};
      users.forEach((user) => {
        if (! uniqueUsers[user.id]) {
          uniqueUsers[user.id] = angular.copy(user);
          usersOccurence[user.id] = 0;
        }
        usersOccurence[user.id] += 1;
      });

      // GroupUsers
      const groupUsers = subTasksAllUsers.filter(user => user.group_id);
      const uniqueGroupUsers = {};
      const groupUsersOccurence = {};
      groupUsers.forEach((user) => {
        if (! uniqueGroupUsers[user.group_id]) {
          uniqueGroupUsers[user.group_id] = angular.copy(user);
          groupUsersOccurence[user.group_id] = 0;
        }
        groupUsersOccurence[user.group_id] += 1;
      });

      // FreeUsers
      const freeUsers = subTasksAllUsers.filter(user => user.username);
      const uniqueFreeUsers = {};
      const freeUsersOccurence = {};
      freeUsers.forEach((user) => {
        if (! uniqueFreeUsers[user.username]) {
          uniqueFreeUsers[user.username] = angular.copy(user);
          freeUsersOccurence[user.username] = 0;
        }
        freeUsersOccurence[user.username] += 1;
      });

      const uniqueUsersVals = Object.values(uniqueUsers);
      const uniqueGroupUsersVals = Object.values(uniqueGroupUsers);
      const uniqueFreeUsersVals = Object.values(uniqueFreeUsers);
      const allUniqueUsers = uniqueUsersVals.concat(uniqueGroupUsersVals).concat(uniqueFreeUsersVals);
      return allUniqueUsers;
    });

    const macroLinks = computed(() => {
      const el = elRef?.value;
      if (! el.isType('macro')) return null;
      if (! macroElements.value.length) return null;
      const subTasksLinks = macroElements.value.map(subTask => subTask.getLinks());
      const subTasksAllLinks = [].concat(...subTasksLinks.filter(links => links?.length));
      const linksName = subTasksAllLinks.map(link => link.name);
      const uniqueLinks = subTasksAllLinks.filter(({ name }, index) => ! linksName.includes(name, index + 1));
      return uniqueLinks;
    });

    const macroBudgets = computed(() => {
      const el = elRef?.value;
      if (! el.isType('macro')) return null;
      if (! macroElements.value.length) return null;
      const subTasksBudgets = macroElements.value.map(subTask => subTask.getBudgets());
      const subTasksAllBudgets = [].concat(...subTasksBudgets.filter(budgets => budgets?.length));
      return subTasksAllBudgets;
    });

    return {
      macroStartTime,
      macroEndTime,
      macroProgress,
      macroUsers,
      macroLinks,
      macroBudgets,
    };
  };

  const watchAndUpdateMacroElement = (elRef) => {
    const { macroStartTime, macroEndTime, macroProgress, macroUsers, macroLinks, macroBudgets } = createMacroComputed(elRef);

    watch(macroStartTime, () => {
      const el = elRef?.value;
      if (! el.isType('macro')) return;
      el.setStartTime(macroStartTime.value);
      el.update();
    });

    watch(macroEndTime, () => {
      const el = elRef?.value;
      if (! el.isType('macro')) return;
      el.setEndTime(macroEndTime.value);
      el.update();
    });

    watch(macroProgress, () => {
      const el = elRef?.value;
      if (! el.isType('macro')) return;
      el.setProgress(macroProgress.value);
      el.update();
    }, { immediate: true });

    watch(macroUsers, () => {
      const el = elRef?.value;
      if (! el.isType('macro')) return;
      if (! angular.equals(el.getUsers(), macroUsers.value)) { // avoid infinite loop : setting new array triggers computed
        el.setUsers(macroUsers.value);
        el.update();
      }
    }, { immediate: true });

    watch(macroLinks, () => {
      const el = elRef?.value;
      if (! el.isType('macro')) return;
      if (! angular.equals(el.getLinks(), macroLinks.value)) { // avoid infinite loop : setting new array triggers computed
        el.setLinks(macroLinks.value);
        el.update();
      }
    }, { immediate: true });

    watch(macroBudgets, () => {
      const el = elRef?.value;
      if (! el.isType('macro')) return;
      if (! angular.equals(el.getBudgets(), macroBudgets.value)) { // avoid infinite loop : setting new array triggers computed
        el.setBudgets(macroBudgets.value);
        el.update();
      }
    }, { immediate: true });
  };

  const updateMacroElementOnce = (el) => {
    if (! el.isType('macro')) return;

    // This helper can be called outside of setup()
    // Use an effectScope so the one-shot computed effects are all cleaned up after use
    const scope = effectScope();

    const { startTime, endTime, progress, users, links, budgets } = scope.run(() => {
      const elRef = ref(el);
      const { macroStartTime, macroEndTime, macroProgress, macroUsers, macroLinks, macroBudgets } = createMacroComputed(elRef);
      return {
        startTime: macroStartTime.value,
        endTime: macroEndTime.value,
        progress: macroProgress.value,
        users: macroUsers.value,
        links: macroLinks.value,
        budgets: macroBudgets.value,
      };
    });
    scope.stop();

    el.setStartTime(startTime);
    el.setEndTime(endTime);
    el.setProgress(progress);
    if (! angular.equals(el.getUsers(), users)) el.setUsers(users);
    if (! angular.equals(el.getLinks(), links)) el.setLinks(links);
    if (! angular.equals(el.getBudgets(), budgets)) el.setBudgets(budgets);
    el.update();
  };

  return {
    watchAndUpdateMacroElement,
    updateMacroElementOnce,
  };
}
